import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatFocusProvider } from "@/contexts/ChatFocusContext";
import { RoleModeProvider } from "@/contexts/RoleModeContext";
import { VibeProvider } from "@/contexts/VibeContext";
import { ActiveCompanyProvider } from "@/contexts/ActiveCompanyContext";
import { CompanyDataProvider } from "@/contexts/CompanyDataContext";
import { ProfileMessagingProvider } from "@/contexts/ProfileMessagingContext";
// StripeProvider removed from global scope - loaded only on payment pages
import { VoiceContextProvider } from "@/contexts/VoiceContextProvider";
import { BackgroundProvider } from "@/contexts/BackgroundContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { EmployerRoute } from "./components/EmployerRoute";
import { SuperAdminRoute } from "./components/SuperAdminRoute";

import { DevModeIndicator } from "./components/DevModeIndicator";
import { usePlatformFavicon } from "./hooks/usePlatformFavicon";
import { AdminLayout } from "./components/admin/AdminLayout";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { PerformancePrefetcher } from "./components/performance/PerformancePrefetcher";
import { PageTransition } from "./components/performance/PageTransition";
import { TopLoadingBar } from "./components/performance/TopLoadingBar";
import { useRealtimeMessageNotifications } from "@/hooks/messaging/useRealtimeMessageNotifications";
import { useRoutePreloader } from "@/hooks/useRoutePreloader";
import { ScrollToTop } from "./components/routing/ScrollToTop";
import { SuspenseLoader } from "./components/routing/SuspenseLoader";
import { RouteErrorBoundary } from "./components/routing/RouteErrorBoundary";
import { VersionChecker } from "./components/routing/VersionChecker";
import { WebVitalsMonitor } from "./components/dev/WebVitalsMonitor";
import { AttachmentTelemetryProvider } from "@/hooks/useAttachmentTelemetry";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { FloatingCookieButton } from "./components/legal/FloatingCookieButton";
import { FloatingVoiceButton } from "./components/voice/FloatingVoiceButton";

// Public Pages
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const AuthV2 = lazyWithRetry(() => import("./pages/AuthV2"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const PublicCredentialView = lazyWithRetry(() => import("./pages/PublicCredentialView"));
const SharedDocument = lazyWithRetry(() => import("./pages/SharedDocument"));
const RSVPPage = lazyWithRetry(() => import("./pages/RSVPPage"));
// Welcome page removed - simplified auth flow goes directly to dashboard

// Core App Pages
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Harmony = lazyWithRetry(() => import("./pages/Harmony"));
const Harmony3 = lazyWithRetry(() => import("./pages/Harmony3"));
const SearchPortal = lazyWithRetry(() => import("./pages/SearchPortal"));
const HarmonyHistory = lazyWithRetry(() => import("./pages/HarmonyHistory"));
const AIConversation = lazyWithRetry(() => import("./pages/AIConversation"));
const DocsPortal = lazyWithRetry(() => import("./pages/DocsPortal"));
const MyEvents = lazyWithRetry(() => import("./pages/MyEvents"));
const MyPeople = lazyWithRetry(() => import("./pages/MyPeople"));
const UniversalPersonPage = lazyWithRetry(() => import("./pages/UniversalPersonPage"));
const RelationshipGraph = lazyWithRetry(() => import("./pages/RelationshipGraph"));
const Connections = lazyWithRetry(() => import("./pages/Connections"));
const ConnectionRequests = lazyWithRetry(() => import("./pages/ConnectionRequests"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const MentionsCenter = lazyWithRetry(() => import("./pages/MentionsCenter"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Finance = lazyWithRetry(() => import("./pages/Finance"));
const Delegations = lazyWithRetry(() => import("./pages/Delegations"));

// Profile & Settings
const MyProfile = lazyWithRetry(() => import("./pages/MyProfile"));

const PublicProfile = lazyWithRetry(() => import("./pages/PublicProfile"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Identity = lazyWithRetry(() => import("./pages/Identity"));
const MyDocuments = lazyWithRetry(() => import("./pages/MyDocuments"));

// Employer Pages
const EmployerATS = lazyWithRetry(() => import("./pages/EmployerATS"));
const EmployerSetup = lazyWithRetry(() => import("./pages/EmployerSetup"));
const EmployerSuccess = lazyWithRetry(() => import("./pages/EmployerSuccess"));
const RequestEmployerAccess = lazyWithRetry(() => import("./pages/RequestEmployerAccess"));
const CompanyTeamApprovals = lazyWithRetry(() => import("./pages/employer/CompanyTeamApprovals"));
const PurchaseTokens = lazyWithRetry(() => import("./pages/employer/PurchaseTokens"));
const EmployerDashboard = lazyWithRetry(() => import("./pages/employer/EmployerDashboard"));

// ATS Pages
const ATSCandidates = lazyWithRetry(() => import("./pages/ats/ATSCandidates"));
const ATSJobs = lazyWithRetry(() => import("./pages/ats/ATSJobs"));
const ATSAnalytics = lazyWithRetry(() => import("./pages/ats/ATSAnalytics"));


// Job Pages
const Jobs = lazyWithRetry(() => import("./pages/Jobs"));
const JobPage = lazyWithRetry(() => import("./pages/JobPage"));
const JobDetailsPage = lazyWithRetry(() => import("./pages/JobDetailsPage"));
const CreateJob = lazyWithRetry(() => import("./pages/CreateJob"));
const JobSuccess = lazyWithRetry(() => import("./pages/JobSuccess"));

// Interview Pages
const Interviews = lazyWithRetry(() => import("./pages/Interviews"));
const InterviewDetail = lazyWithRetry(() => import("./pages/InterviewDetail"));

// Application Detail
const ApplicationDetail = lazyWithRetry(() => import("./pages/ApplicationDetail"));

// Company Pages
const PublicCompanyProfile = lazyWithRetry(() => import("./pages/PublicCompanyProfile"));
const CompanyCareerSite = lazyWithRetry(() => import("./pages/CompanyCareerSite"));
const Companies = lazyWithRetry(() => import("./pages/Companies"));

// Invitation Pages
const AcceptInvitation = lazyWithRetry(() => import("./pages/AcceptInvitation"));
const AcceptNetworkInvitation = lazyWithRetry(() => import("./pages/AcceptNetworkInvitation"));

// Billing & Subscription
const Billing = lazyWithRetry(() => import("./pages/Billing"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const Wallet = lazyWithRetry(() => import("./pages/Wallet"));

// Utility Pages
const PrivacyExplainer = lazyWithRetry(() => import("./pages/PrivacyExplainer"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const About = lazyWithRetry(() => import("./pages/About"));
const AccountCreated = lazyWithRetry(() => import("./pages/AccountCreated"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const LoginHistory = lazyWithRetry(() => import("./pages/LoginHistory"));
const DesignSystem = lazyWithRetry(() => import("./pages/DesignSystem"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// Admin Pages
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminModules = lazyWithRetry(() => import("./pages/admin/AdminModules"));
const AdminIntegrations = lazyWithRetry(() => import("./pages/admin/AdminIntegrations"));
const Analytics = lazyWithRetry(() => import("./pages/admin/Analytics"));
const UsersManagement = lazyWithRetry(() => import("./pages/admin/UsersManagement"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AdminCompanies = lazyWithRetry(() => import("./pages/admin/AdminCompanies"));
const AdminCompanyDetail = lazyWithRetry(() => import("./pages/admin/AdminCompanyDetail"));
const AdminEmployerRequests = lazyWithRetry(() => import("./pages/admin/AdminEmployerRequests"));
const AdminJobs = lazyWithRetry(() => import("./pages/admin/AdminJobs"));
const AdminEmailManagement = lazyWithRetry(() => import("./pages/admin/AdminEmailManagement"));
const AdminNotifications = lazyWithRetry(() => import("./pages/admin/AdminNotifications"));
const AdminPartnerships = lazyWithRetry(() => import("./pages/admin/AdminPartnerships"));
const AdminIndustryCategories = lazyWithRetry(() => import("./pages/admin/AdminIndustryCategories"));
const AdminMarketplaceCategories = lazyWithRetry(() => import("./pages/admin/AdminMarketplaceCategories"));
const AdminTechStackRegistry = lazyWithRetry(() => import("./pages/admin/AdminTechStackRegistry"));
// AdminLearnModules removed - Elon-style cleanup
const AppStore = lazyWithRetry(() => import("./pages/admin/AppStore"));
const AIAnalytics = lazyWithRetry(() => import("./pages/admin/AIAnalytics"));
const ChurnAnalytics = lazyWithRetry(() => import("./pages/admin/ChurnAnalytics"));
const ExecutiveAnalytics = lazyWithRetry(() => import("./pages/admin/ExecutiveAnalytics"));
const FunnelAnalytics = lazyWithRetry(() => import("./pages/admin/FunnelAnalytics"));
const UserJourneyAnalytics = lazyWithRetry(() => import("./pages/admin/UserJourneyAnalytics"));
const UserDetailPage = lazyWithRetry(() => import("./pages/admin/UserDetailPage"));
const SMSDelivery = lazyWithRetry(() => import("./pages/admin/SMSDelivery"));
const SystemHealth = lazyWithRetry(() => import("./pages/admin/SystemHealth"));
const ToolMonitor = lazyWithRetry(() => import("./pages/admin/ToolMonitor"));
const WebhookTesting = lazyWithRetry(() => import("./pages/admin/WebhookTesting"));
const PublishErrors = lazyWithRetry(() => import("./pages/admin/PublishErrors"));
const AdminRoadmap = lazyWithRetry(() => import("./pages/admin/AdminRoadmap"));
const AdminChiefOfStaffRoadmap = lazyWithRetry(() => import("./pages/admin/AdminChiefOfStaffRoadmap"));
const AdminDocumentation = lazyWithRetry(() => import("./pages/admin/AdminDocumentation"));
const AdminDocViewer = lazyWithRetry(() => import("./pages/admin/AdminDocViewer"));
const AdminAITools = lazyWithRetry(() => import("./pages/admin/AdminAITools"));
const AdminGoogleOAuthConfig = lazyWithRetry(() => import("./pages/admin/AdminGoogleOAuthConfig"));
const QADashboard = lazyWithRetry(() => import("./pages/admin/QADashboard"));
const VoiceSecurityDashboard = lazyWithRetry(() => import("./pages/admin/VoiceSecurityDashboard"));

// Legacy route redirect component
function LegacyContactRedirect() {
  const { contactId } = useParams<{ contactId: string }>();
  return <Navigate to={`/person/${contactId}`} replace />;
}
// Developer Portal Pages
const DeveloperPortal = lazyWithRetry(() => import("./pages/developers/DeveloperPortal"));
const DeveloperApps = lazyWithRetry(() => import("./pages/developers/DeveloperApps"));
const DeveloperAPIKeys = lazyWithRetry(() => import("./pages/developers/DeveloperAPIKeys"));
const DeveloperAPIExplorer = lazyWithRetry(() => import("./pages/developers/DeveloperAPIExplorer"));
const DeveloperGettingStarted = lazyWithRetry(() => import("./pages/developers/DeveloperGettingStarted"));
const DeveloperWebhooks = lazyWithRetry(() => import("./pages/developers/DeveloperWebhooks"));
const DeveloperLogs = lazyWithRetry(() => import("./pages/developers/DeveloperLogs"));
const DeveloperUsage = lazyWithRetry(() => import("./pages/developers/DeveloperUsage"));

// Admin AI
const AdminAIManagement = lazyWithRetry(() => import("./pages/admin/ai/AdminAIManagement"));
const AdminAIProviders = lazyWithRetry(() => import("./pages/admin/ai/AdminAIProviders"));
const AdminAIUsage = lazyWithRetry(() => import("./pages/admin/ai/AdminAIUsage"));
const AITraining = lazyWithRetry(() => import("./pages/admin/ai/AITraining"));
const VoiceLatency = lazyWithRetry(() => import("./pages/admin/VoiceLatency"));

// Admin Commerce
const AdminCompanyWallets = lazyWithRetry(() => import("./pages/admin/commerce/AdminCompanyWallets"));
const AdminDiscounts = lazyWithRetry(() => import("./pages/admin/commerce/AdminDiscounts"));
const AdminEmployerProducts = lazyWithRetry(() => import("./pages/admin/commerce/AdminEmployerProducts"));
const AdminHiringTokens = lazyWithRetry(() => import("./pages/admin/commerce/AdminHiringTokens"));
const AdminJobSeekerProducts = lazyWithRetry(() => import("./pages/admin/commerce/AdminJobSeekerProducts"));
const AdminOrders = lazyWithRetry(() => import("./pages/admin/commerce/AdminOrders"));
const AdminPaymentMethods = lazyWithRetry(() => import("./pages/admin/commerce/AdminPaymentMethods"));

// RouterContent - Lives inside BrowserRouter so router hooks work
function RouterContent() {
  useRoutePreloader(); // Safe to call here - inside BrowserRouter
  useRealtimeMessageNotifications();

  return (
    <>
      <AnalyticsProvider>
        <VersionChecker />
        <PerformancePrefetcher />
        <ScrollToTop />
        <TopLoadingBar />
        <DevModeIndicator />
        {/* Floating Cookie Preferences Button - World-class persistent access */}
        <FloatingCookieButton />
        {/* Floating Voice Button - Global voice access (Phase 1 UX) */}
        <FloatingVoiceButton />
        {/* Web Vitals Monitor - Dev only */}
        {import.meta.env.DEV && <WebVitalsMonitor position="bottom-left" />}

        <Suspense fallback={<SuspenseLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<RouteErrorBoundary><Index /></RouteErrorBoundary>} />
            <Route path="/auth" element={<PageTransition><AuthV2 /></PageTransition>} />
            <Route path="/auth/legacy" element={<PageTransition><Auth /></PageTransition>} />
            <Route path="/auth/callback" element={<PageTransition><AuthCallback /></PageTransition>} />
            <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
            <Route path="/credentials/view/:shareCode" element={<PageTransition><PublicCredentialView /></PageTransition>} />
            <Route path="/shared/:shareCode" element={<PageTransition><SharedDocument /></PageTransition>} />
            <Route path="/rsvp" element={<PageTransition><RSVPPage /></PageTransition>} />

            {/* Public Harmony route - auth-gating happens within the component */}
            <Route path="/harmony" element={<RouteErrorBoundary><Harmony /></RouteErrorBoundary>} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              {/* Welcome removed - redirect to dashboard for backwards compatibility */}
              <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<RouteErrorBoundary><PageTransition><Dashboard /></PageTransition></RouteErrorBoundary>} />
              <Route path="/harmony_2" element={<Navigate to="/" replace />} />
              <Route path="/harmony3" element={<RouteErrorBoundary><PageTransition><Harmony3 /></PageTransition></RouteErrorBoundary>} />
              <Route path="/search-portal" element={<RouteErrorBoundary><PageTransition><SearchPortal /></PageTransition></RouteErrorBoundary>} />
              <Route path="/harmony/history" element={<RouteErrorBoundary><PageTransition><HarmonyHistory /></PageTransition></RouteErrorBoundary>} />
              <Route path="/chat" element={<RouteErrorBoundary><AIConversation /></RouteErrorBoundary>} />
              <Route path="/documentation" element={<Navigate to="/docs-portal" replace />} />
              <Route path="/docs-portal/*" element={<PageTransition><DocsPortal /></PageTransition>} />
              {/* Matches and Applications deactivated - redirect to dashboard */}
              <Route path="/matches" element={<Navigate to="/dashboard" replace />} />
              <Route path="/applications" element={<Navigate to="/dashboard" replace />} />
              <Route path="/applications/performance" element={<Navigate to="/dashboard" replace />} />
              <Route path="/my-events" element={<PageTransition><MyEvents /></PageTransition>} />
              {/* My People - renamed from Network */}
              <Route path="/people" element={<RouteErrorBoundary><PageTransition><MyPeople /></PageTransition></RouteErrorBoundary>} />
              {/* Legacy route - redirect /network to /people */}
              <Route path="/network" element={<Navigate to="/people" replace />} />
              {/* Legacy route - redirect to universal person page */}
              <Route path="/network/contact/:contactId" element={<LegacyContactRedirect />} />
              <Route path="/person/:personId" element={<RouteErrorBoundary><PageTransition><UniversalPersonPage /></PageTransition></RouteErrorBoundary>} />
              <Route path="/person/:personId" element={<RouteErrorBoundary><PageTransition><UniversalPersonPage /></PageTransition></RouteErrorBoundary>} />
              <Route path="/network/graph" element={<RouteErrorBoundary><PageTransition><RelationshipGraph /></PageTransition></RouteErrorBoundary>} />
              <Route path="/finance" element={<RouteErrorBoundary><PageTransition><Finance /></PageTransition></RouteErrorBoundary>} />
              <Route path="/delegations" element={<RouteErrorBoundary><PageTransition><Delegations /></PageTransition></RouteErrorBoundary>} />
              <Route path="/connections" element={<PageTransition><Connections /></PageTransition>} />
              <Route path="/connection-requests" element={<PageTransition><ConnectionRequests /></PageTransition>} />
              <Route path="/messages" element={<ProfileMessagingProvider><RouteErrorBoundary><PageTransition><Messages /></PageTransition></RouteErrorBoundary></ProfileMessagingProvider>} />
              <Route path="/mentions" element={<RouteErrorBoundary><PageTransition><MentionsCenter /></PageTransition></RouteErrorBoundary>} />
              <Route path="/notifications" element={<RouteErrorBoundary><PageTransition><Notifications /></PageTransition></RouteErrorBoundary>} />
              <Route path="/my-profile" element={<RouteErrorBoundary><PageTransition><MyProfile /></PageTransition></RouteErrorBoundary>} />
              
              <Route path="/profile/:id" element={<PageTransition><PublicProfile /></PageTransition>} />
              <Route path="/identity" element={<RouteErrorBoundary><PageTransition><Identity /></PageTransition></RouteErrorBoundary>} />
              <Route path="/my-documents" element={<RouteErrorBoundary><PageTransition><MyDocuments /></PageTransition></RouteErrorBoundary>} />
              <Route path="/settings/:section?/:subsection?" element={<RouteErrorBoundary><PageTransition><Settings /></PageTransition></RouteErrorBoundary>} />
              <Route path="/request-employer-access" element={<PageTransition><RequestEmployerAccess /></PageTransition>} />
              <Route path="/employer-setup" element={<RouteErrorBoundary><PageTransition><EmployerSetup /></PageTransition></RouteErrorBoundary>} />
              <Route path="/employer-setup/:companyId" element={<RouteErrorBoundary><PageTransition><EmployerSetup /></PageTransition></RouteErrorBoundary>} />
              <Route path="/employer-success" element={<RouteErrorBoundary><PageTransition><EmployerSuccess /></PageTransition></RouteErrorBoundary>} />
            </Route>

            {/* Employer Routes */}
            <Route path="/employer" element={<Navigate to="/ats" replace />} />
            <Route path="/ats" element={<EmployerRoute><RouteErrorBoundary><PageTransition><EmployerATS /></PageTransition></RouteErrorBoundary></EmployerRoute>} />
            <Route path="/ats/jobs" element={<EmployerRoute><RouteErrorBoundary><PageTransition><ATSJobs /></PageTransition></RouteErrorBoundary></EmployerRoute>} />
            <Route path="/ats/candidates" element={<EmployerRoute><RouteErrorBoundary><PageTransition><ATSCandidates /></PageTransition></RouteErrorBoundary></EmployerRoute>} />
            <Route path="/ats/analytics" element={<EmployerRoute><RouteErrorBoundary><PageTransition><ATSAnalytics /></PageTransition></RouteErrorBoundary></EmployerRoute>} />
            <Route path="/employer-dashboard" element={<EmployerRoute><RouteErrorBoundary><PageTransition><EmployerDashboard /></PageTransition></RouteErrorBoundary></EmployerRoute>} />
            <Route path="/employer/approvals" element={<EmployerRoute><PageTransition><CompanyTeamApprovals /></PageTransition></EmployerRoute>} />
            <Route path="/employer/purchase" element={<EmployerRoute><PageTransition><PurchaseTokens /></PageTransition></EmployerRoute>} />

            {/* Job Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/jobs" element={<PageTransition><Jobs /></PageTransition>} />
              <Route path="/jobs/:id" element={<PageTransition><JobPage /></PageTransition>} />
              <Route path="/job-detail/:id" element={<PageTransition><JobDetailsPage /></PageTransition>} />
              <Route path="/applications/:id" element={<Navigate to="/dashboard" replace />} />
            </Route>
            <Route path="/create-job" element={<EmployerRoute><PageTransition><CreateJob /></PageTransition></EmployerRoute>} />
            <Route path="/job-success" element={<PageTransition><JobSuccess /></PageTransition>} />

            {/* Interview Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/interviews" element={<PageTransition><Interviews /></PageTransition>} />
              <Route path="/interviews/:id" element={<PageTransition><InterviewDetail /></PageTransition>} />
            </Route>

            {/* Company Routes */}
            <Route path="/company/:id" element={<PageTransition><PublicCompanyProfile /></PageTransition>} />
            <Route path="/company/:id/career-site" element={<PageTransition><CompanyCareerSite /></PageTransition>} />
            <Route path="/companies" element={<PageTransition><Companies /></PageTransition>} />

            {/* Invitation Routes */}
            <Route path="/accept-invitation" element={<PageTransition><AcceptInvitation /></PageTransition>} />
            <Route path="/accept-network-invitation" element={<PageTransition><AcceptNetworkInvitation /></PageTransition>} />

            {/* Billing & Subscription Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/billing" element={<PageTransition><Billing /></PageTransition>} />
              <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
              <Route path="/subscription" element={<Navigate to="/wallet" replace />} />
              <Route path="/wallet" element={<RouteErrorBoundary><PageTransition><Wallet /></PageTransition></RouteErrorBoundary>} />
              
              {/* Redirects from old routes */}
              <Route path="/credits" element={<Navigate to="/wallet" replace />} />
              <Route path="/ai-credits" element={<Navigate to="/wallet" replace />} />
              <Route path="/pricing" element={<Navigate to="/wallet" replace />} />
            </Route>

            {/* Developer Portal Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/developers" element={<PageTransition><DeveloperPortal /></PageTransition>} />
              <Route path="/developers/getting-started" element={<PageTransition><DeveloperGettingStarted /></PageTransition>} />
              <Route path="/developers/apps" element={<PageTransition><DeveloperApps /></PageTransition>} />
              <Route path="/developers/api-keys" element={<PageTransition><DeveloperAPIKeys /></PageTransition>} />
              <Route path="/developers/explorer" element={<PageTransition><DeveloperAPIExplorer /></PageTransition>} />
              <Route path="/developers/webhooks" element={<PageTransition><DeveloperWebhooks /></PageTransition>} />
              <Route path="/developers/logs" element={<PageTransition><DeveloperLogs /></PageTransition>} />
              <Route path="/developers/usage" element={<PageTransition><DeveloperUsage /></PageTransition>} />
            </Route>

            {/* Utility Routes */}
            <Route path="/privacy-explainer" element={<PageTransition><PrivacyExplainer /></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
            <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
            <Route path="/about" element={<PageTransition><About /></PageTransition>} />
            <Route path="/account-created" element={<PageTransition><AccountCreated /></PageTransition>} />
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
              <Route path="/login-history" element={<PageTransition><LoginHistory /></PageTransition>} />
              <Route path="/design-system" element={<PageTransition><DesignSystem /></PageTransition>} />
            </Route>

            {/* Vendor Routes - Redirect to dashboard for bookmarked URLs */}
            <Route path="/vendor/*" element={<Navigate to="/dashboard" replace />} />

            {/* Admin Routes */}
            <Route element={<SuperAdminRoute />}>
              <Route path="/admin" element={<AdminLayout showHeader={true} showNavTabs={false}><Navigate to="/admin/dashboard" replace /></AdminLayout>} />
              
              {/* Core Admin */}
              <Route path="/admin/dashboard" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminDashboard /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/roadmap" element={<RouteErrorBoundary><PageTransition><AdminRoadmap /></PageTransition></RouteErrorBoundary>} />
              <Route path="/admin/chief-of-staff" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminChiefOfStaffRoadmap /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/modules" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminModules /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/analytics" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><Analytics /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><UsersManagement /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/users/:id" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><UserDetailPage /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/settings" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminSettings /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/system-health" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><SystemHealth /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/qa-dashboard" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><QADashboard /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/tool-monitor" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><ToolMonitor /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/webhook-testing" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><WebhookTesting /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              
              {/* Company Management */}
              <Route path="/admin/companies" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminCompanies /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/companies/:id" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminCompanyDetail /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              
              {/* Employer & Vendor */}
              <Route path="/admin/employer-requests" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminEmployerRequests /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              {/* Vendor routes removed - redirect for bookmarked URLs */}
              <Route path="/admin/vendor-applications" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/vendors" element={<Navigate to="/admin/dashboard" replace />} />
              
              {/* Job Management */}
              <Route path="/admin/jobs" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AdminJobs /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              
              {/* Categories & Registry */}
              <Route path="/admin/industry-categories" element={<AdminLayout showHeader={true}><PageTransition><AdminIndustryCategories /></PageTransition></AdminLayout>} />
              <Route path="/admin/marketplace-categories" element={<AdminLayout showHeader={true}><PageTransition><AdminMarketplaceCategories /></PageTransition></AdminLayout>} />
              <Route path="/admin/tech-stack" element={<AdminLayout showHeader={true}><PageTransition><AdminTechStackRegistry /></PageTransition></AdminLayout>} />
              <Route path="/admin/integrations" element={<AdminLayout showHeader={true}><PageTransition><AdminIntegrations /></PageTransition></AdminLayout>} />
              <Route path="/admin/integrations/google-oauth" element={<AdminLayout showHeader={true}><PageTransition><AdminGoogleOAuthConfig /></PageTransition></AdminLayout>} />
              
              {/* Communication */}
              <Route path="/admin/email" element={<AdminLayout showHeader={true}><PageTransition><AdminEmailManagement /></PageTransition></AdminLayout>} />
              <Route path="/admin/sms" element={<AdminLayout showHeader={true}><PageTransition><SMSDelivery /></PageTransition></AdminLayout>} />
              <Route path="/admin/notifications" element={<AdminLayout showHeader={true}><PageTransition><AdminNotifications /></PageTransition></AdminLayout>} />
              
              {/* Partnerships */}
              <Route path="/admin/partnerships" element={<AdminLayout showHeader={true}><PageTransition><AdminPartnerships /></PageTransition></AdminLayout>} />
              {/* Learn Modules removed - redirect for bookmarked URLs */}
              <Route path="/admin/learn-modules" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/app-store" element={<AdminLayout showHeader={true}><PageTransition><AppStore /></PageTransition></AdminLayout>} />
              <Route path="/admin/publish-errors" element={<AdminLayout showHeader={true}><PageTransition><PublishErrors /></PageTransition></AdminLayout>} />
              
              {/* Documentation */}
              <Route path="/admin/documents" element={<AdminLayout showHeader={true}><PageTransition><AdminDocumentation /></PageTransition></AdminLayout>} />
              <Route path="/admin/documents/*" element={<AdminLayout showHeader={true}><PageTransition><AdminDocViewer /></PageTransition></AdminLayout>} />
              
              {/* Analytics */}
              <Route path="/admin/analytics/ai" element={<AdminLayout showHeader={true}><PageTransition><AIAnalytics /></PageTransition></AdminLayout>} />
              <Route path="/admin/analytics/churn" element={<AdminLayout showHeader={true}><PageTransition><ChurnAnalytics /></PageTransition></AdminLayout>} />
              <Route path="/admin/analytics/executive" element={<AdminLayout showHeader={true}><PageTransition><ExecutiveAnalytics /></PageTransition></AdminLayout>} />
              <Route path="/admin/analytics/funnel" element={<AdminLayout showHeader={true}><PageTransition><FunnelAnalytics /></PageTransition></AdminLayout>} />
              <Route path="/admin/analytics/user-journey" element={<AdminLayout showHeader={true}><PageTransition><UserJourneyAnalytics /></PageTransition></AdminLayout>} />
              <Route path="/admin/analytics/voice-latency" element={<AdminLayout showHeader={true}><PageTransition><VoiceLatency /></PageTransition></AdminLayout>} />
              <Route path="/admin/voice-security" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><VoiceSecurityDashboard /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              
              {/* AI Management */}
              <Route path="/admin/ai" element={<AdminLayout showHeader={true}><PageTransition><AdminAIManagement /></PageTransition></AdminLayout>} />
              <Route path="/admin/ai/providers" element={<AdminLayout showHeader={true}><PageTransition><AdminAIProviders /></PageTransition></AdminLayout>} />
              <Route path="/admin/ai/usage" element={<AdminLayout showHeader={true}><PageTransition><AdminAIUsage /></PageTransition></AdminLayout>} />
              <Route path="/admin/ai/training" element={<AdminLayout showHeader={true}><RouteErrorBoundary><PageTransition><AITraining /></PageTransition></RouteErrorBoundary></AdminLayout>} />
              <Route path="/admin/ai-tools" element={<AdminLayout showHeader={true}><PageTransition><AdminAITools /></PageTransition></AdminLayout>} />
              
              {/* Commerce */}
              <Route path="/admin/commerce/wallets" element={<AdminLayout showHeader={true}><PageTransition><AdminCompanyWallets /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/discounts" element={<AdminLayout showHeader={true}><PageTransition><AdminDiscounts /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/employer-products" element={<AdminLayout showHeader={true}><PageTransition><AdminEmployerProducts /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/hiring-tokens" element={<AdminLayout showHeader={true}><PageTransition><AdminHiringTokens /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/jobseeker-products" element={<AdminLayout showHeader={true}><PageTransition><AdminJobSeekerProducts /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/orders" element={<AdminLayout showHeader={true}><PageTransition><AdminOrders /></PageTransition></AdminLayout>} />
              <Route path="/admin/commerce/payment-methods" element={<AdminLayout showHeader={true}><PageTransition><AdminPaymentMethods /></PageTransition></AdminLayout>} />
            </Route>

            {/* Catch-all - 404 Not Found */}
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Routes>
        </Suspense>
      </AnalyticsProvider>
    </>
  );
}

function App() {
  usePlatformFavicon();

  return (
    <BrowserRouter>
      <AuthProvider>
        <ChatFocusProvider>
          <BackgroundProvider>
            <VoiceContextProvider>
              <RoleModeProvider>
                <VibeProvider>
                  <ActiveCompanyProvider>
                    <CompanyDataProvider>
                      <TooltipProvider>
                        <AttachmentTelemetryProvider>
                          <RouterContent />
                        </AttachmentTelemetryProvider>
                      </TooltipProvider>
                    </CompanyDataProvider>
                  </ActiveCompanyProvider>
                </VibeProvider>
              </RoleModeProvider>
            </VoiceContextProvider>
          </BackgroundProvider>
        </ChatFocusProvider>
      </AuthProvider>
      <Toaster />
      <Sonner />
    </BrowserRouter>
  );
}

export default App;
